import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { Stack } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { FilterBar } from '../../components/FilterBar';
import { DataTable, type Column } from '../../components/DataTable';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import { StatusBadge } from '../../components/StatusBadge';
import { usePayrollRuns, usePayrollRun } from '../../hooks/usePayroll';
import { formatDate } from '../../utils/format';

interface PayStubRow {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  payPeriodLabel: string;
  grossPay: number;
  netPay: number;
  status: string;
  [key: string]: unknown;
}

export function PayStubListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const pageSize = 20;

  const { data: runsData } = usePayrollRuns({ page: 1, perPage: 100 });

  const runs = useMemo(() => {
    if (!runsData) return [];
    const raw = runsData as { data?: Array<{ id: string; status: string; payPeriodId: string; createdAt: string }> };
    return raw.data ?? [];
  }, [runsData]);

  const selectedRunId = filterValues.payrollRun ?? (runs.length > 0 ? runs[0]?.id ?? '' : '');

  const { data: runQueryData, loading: isLoading } = usePayrollRun(selectedRunId);
  const runDetail = runQueryData?.payrollRun;

  const payStubs: PayStubRow[] = useMemo(() => {
    if (!runDetail?.payStubs) return [];
    return runDetail.payStubs.map((stub: { id: string; employeeId: string; employeeName: string; grossPay: number; netPay: number }, idx: number) => ({
      id: stub.id,
      employeeId: stub.employeeId,
      employeeName: stub.employeeName,
      employeeNumber: String(idx + 1).padStart(4, '0'),
      payPeriodLabel: selectedRunId,
      grossPay: stub.grossPay,
      netPay: stub.netPay,
      status: runDetail.status,
    }));
  }, [runDetail, selectedRunId]);

  const filtered = useMemo(() => {
    if (!search) return payStubs;
    const q = search.toLowerCase();
    return payStubs.filter(
      (s) =>
        s.employeeName.toLowerCase().includes(q) ||
        s.employeeNumber.toLowerCase().includes(q),
    );
  }, [payStubs, search]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page],
  );

  const columns: Column<PayStubRow>[] = [
    { key: 'employeeName', label: 'Employee', sortable: true },
    { key: 'employeeNumber', label: 'Employee #', sortable: true },
    { key: 'payPeriodLabel', label: 'Pay Period' },
    {
      key: 'grossPay',
      label: 'Gross Pay',
      render: (row) => <CurrencyDisplay amount={row.grossPay} />,
      sortable: true,
    },
    {
      key: 'netPay',
      label: 'Net Pay',
      render: (row) => <CurrencyDisplay amount={row.netPay} />,
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  const runOptions = runs.map((r) => ({
    value: r.id,
    label: `${r.id} (${r.status})`,
  }));

  return (
    <Stack>
      <PageHeader
        title="Pay Stubs"
        breadcrumbs={[
          { label: 'Payroll', to: '/admin/workforce/payroll' },
          { label: 'Pay Stubs' },
        ]}
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by employee name or number..."
        filters={[
          {
            key: 'payrollRun',
            label: 'Payroll Run',
            options: runOptions,
            clearable: true,
          },
        ]}
        onFilterChange={(key, val) =>
          setFilterValues((prev) => ({ ...prev, [key]: val }))
        }
        filterValues={filterValues}
      />

      <DataTable<PayStubRow>
        data={paged}
        columns={columns}
        loading={isLoading}
        getRowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/payroll/pay-stubs/${row.id}`)}
        emptyMessage="No pay stubs found for this payroll run"
        pagination={{
          page,
          pageSize,
          total: filtered.length,
          onPageChange: setPage,
        }}
      />
    </Stack>
  );
}
